import {useCallback, useEffect, useRef, useState} from 'react';
import {
  REPORT_POLL_INTERVAL_MS,
  REPORT_POLL_TIMEOUT_MS,
  isGqlError,
} from '../constants';

const IDLE = {status: 'idle', message: ''};

/**
 * Watches for an asynchronously generated PDF.
 *
 * Generation returns "queued", not "uploaded", and there is no job id or status
 * endpoint. A regenerated PDF also reuses its storage key, so an unchanged URL
 * proves nothing — which is why a group that already has a URL is reported as
 * accepted rather than polled to a false "complete".
 */
export const useReportPolling = refetch => {
  const [reportState, setReportState] = useState(IDLE);
  const timer = useRef();

  const stop = useCallback(() => clearInterval(timer.current), []);

  const startReportWatch = useCallback(
    ({hasExistingReport}) => {
      stop();
      if (hasExistingReport) {
        setReportState({
          status: 'accepted',
          message:
            'Request accepted. The existing link is reused — open it again shortly to see the new version.',
        });
        return;
      }

      const startedAt = Date.now();
      setReportState({status: 'polling', message: 'Generating…'});
      timer.current = setInterval(async () => {
        const {data} = await refetch();
        const result = data?.getInstrumentRecommendationPicks;
        const reports = isGqlError(result) ? [] : result?.data?.reports || [];
        if (reports.length) {
          stop();
          setReportState({status: 'ready', message: 'Report ready.'});
        } else if (Date.now() - startedAt > REPORT_POLL_TIMEOUT_MS) {
          stop();
          setReportState({
            status: 'timeout',
            message: 'Generation is still processing; refresh later.',
          });
        }
      }, REPORT_POLL_INTERVAL_MS);
    },
    [refetch, stop],
  );

  const clearReportWatch = useCallback(() => {
    stop();
    setReportState(IDLE);
  }, [stop]);

  useEffect(() => () => clearInterval(timer.current), []);

  return {reportState, startReportWatch, clearReportWatch};
};

export default useReportPolling;
